import { useCallback } from "react";
import { useSelector } from "react-redux";
import { IRenderFunction } from "@fluentui/react/lib/Utilities";
import { Pivot, PivotItem, IPivotItemProps } from "@fluentui/react/lib/Pivot";

import { WithTranslator } from "Components/Generic/Translator";
import { selectFilterCount } from "Store/Selectors/Filter";
import { hasNoElements } from "Utils/Array";
import { WithConfiguration } from "Components/Generic/Configuration";
import { Icon, Stack, Text } from "@fluentui/react";
import { WithTheme } from "Components/Generic/Theme/WithTheme";
import { useFilter } from "./UseFilter";
import { Badge } from "./Badge";
import { IFilterProps } from "./IFilterProps";
import { useFilterStyles } from "./UseFilterStyles";

export const Filter = WithTheme(
  WithConfiguration(
    WithTranslator((props: IFilterProps): JSX.Element => {
      const {
        config,
        translator,
        theme: { spacing }
      } = props;
      const { onFilterClick, selectedKey } = useFilter();
      const filterCount = useSelector(selectFilterCount);
      const filterCategoryList = config.getFilterCategoryList();

      const styles = useFilterStyles();

      const onRenderItemLink = useCallback(
        (p?: IPivotItemProps, defaultRender?: IRenderFunction<IPivotItemProps>): JSX.Element => {
          if (hasNoElements(filterCount)) {
            return defaultRender(p);
          }

          return (
            <Stack style={{ width: `100%` }} tokens={{ childrenGap: spacing.s1 }} horizontal verticalAlign="center">
              <Stack.Item>
                <Icon iconName={p.itemIcon} />
              </Stack.Item>
              <Stack.Item grow align="start">
                <Text>{p.headerText}</Text>
              </Stack.Item>
              <Stack.Item>
                <Badge count={filterCount.find(([key]) => key === p.itemKey)[1]} />
              </Stack.Item>
            </Stack>
          );
        },
        [filterCount, spacing.s1]
      );

      return (
        <Pivot
          headersOnly
          className={styles.root}
          selectedKey={selectedKey}
          onLinkClick={onFilterClick}
          overflowBehavior="menu"
        >
          {filterCategoryList.map(({ translationKey, icon }) => (
            <PivotItem
              headerText={translator.get(`overview.${translationKey}`, `~${translationKey}`)}
              itemIcon={icon}
              itemKey={translationKey}
              key={translationKey}
              onRenderItemLink={onRenderItemLink}
            />
          ))}
        </Pivot>
      );
    })
  )
);
